import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { BoxArrowRight, BoxArrowUpRight, Shop, PlusLg, ChevronExpand, Check2 } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { openTopLevel } from '../config/embedded';
import { IS_PROD } from '../config/api';
import { useAuth } from '../auth/AuthContext';
import { fetchSession, linkShopifyStore, shopifyConnectUrl } from '../api/brands';
import PortalShell, { usePortalTheme } from '../components/PortalShell';
import BrandDashboard, { BRAND_NAV, DEFAULT_TAB, TAB_REDIRECTS, landingTab, useBrandTabs } from '../components/BrandDashboard';
import ShopifyConnectionBanner from '../components/ShopifyConnectionBanner';
import PageErrorBoundary from '../components/PageErrorBoundary';
import BrandInactiveBanner from '../components/BrandInactiveBanner';
import BrandSuspendedBanner from '../components/BrandSuspendedBanner';
import { brandChrome } from '../components/brandChrome';

import NewBrandPage from '../components/onboarding/NewBrandPage';
import { sessionBrandFrom, withCreatedBrand } from '../api/onboarding';
import usePortalBadges from '../components/portalBadges';
import { loginPathFor } from '../config/returnTo';
import './Dashboard.scss';

// "Új márka létrehozása" (/dashboard/new-brand): a brand without Shopify —
// name, logo, cover, webshop address, the partner terms (POST /brands), then
// the card, which starts the 1-month free trial (components/onboarding).
export const NEW_BRAND_PAGE = 'new-brand';

// The Shopify connect (/v3/shopify/auth?token=… with no shop, so Shopify's own
// store picker chooses; the embedded app claims the store on arrival — the
// claimUrl bounce below). Offered on the new-brand page only while
// SHOW_SHOPIFY_APP_TUTORIAL is on (the Shopify app is not approved yet).
function startShopifyConnect() {
  window.location.href = shopifyConnectUrl();
}

// The brand last picked in the switcher, so a reload of /dashboard/products
// comes back to that brand's products rather than the first brand's. Storage
// can throw inside the Shopify admin iframe — there is only one brand there.
const BRAND_KEY = 'partners_brand';

const SHOPIFY_APP_CLIENT_ID = IS_PROD ? 'dfbeece1db9bbe0ce85f987e3f5c8de1' : '21776526bdb15466ddb43c34383fc9bc';

function shopifyAdminAppUrl(storeDomain) {
  const match = /^([a-z0-9][a-z0-9-]*)\.myshopify\.com$/i.exec(storeDomain || '');
  return match ? `https://admin.shopify.com/store/${match[1]}/apps/${SHOPIFY_APP_CLIENT_ID}` : null;
}

function readStoredBrand() {
  try {
    return localStorage.getItem(BRAND_KEY);
  } catch {
    return null;
  }
}

function storeBrand(id) {
  try {
    localStorage.setItem(BRAND_KEY, id);
  } catch {
    /* not remembered — see readStoredBrand */
  }
}

export default function Dashboard() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { user, loading, brands: sessionBrands, embedded, returnTo, portalUrl, claimUrl, linked, logout, refresh } = useAuth();
  // Called up here, not in the shell, so the loading screens are themed too.
  const theme = usePortalTheme();

  // Every brand page is a route of its own: /dashboard/products on the portal,
  // /products inside the Shopify admin (App.js). One `/*` route renders this
  // component for all of them, so it stays mounted while pages change — the
  // mount-only connect effects below must not run again on every click.
  const basePath = embedded ? '' : '/dashboard';
  const tabParam = (useParams()['*'] || '').split('/')[0] || null;

  const [selectedBrandId, setSelectedBrandId] = useState(readStoredBrand);
  // Set when we come back from a connect knowing only the shop domain; resolved
  // to a brand id once the refreshed session lists it.
  const [pendingShop, setPendingShop] = useState(null);
  // Shown while the store is being attached to this account after a connect.
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);
  // The brand just made on the new-brand page: shown at once (the session may
  // not list it yet, or list it without the owner's membership), and its card
  // step is the onboarding's second step.
  const [created, setCreated] = useState(null); // { brand, card: bool }
  const [toast, setToast] = useState(null); // { key, leaving }
  const toastTimers = useRef([]);
  useEffect(() => () => toastTimers.current.forEach(clearTimeout), []);
  const brands = useMemo(
    () => withCreatedBrand(sessionBrands, created?.brand, { listed: created?.listed }),
    [sessionBrands, created]
  );
  // Once the session lists the new brand, it is never put back by hand (a
  // deleted brand must leave the switcher).
  useEffect(() => {
    if (created && !created.listed && sessionBrands.some((b) => b.id === created.brand.id)) {
      setCreated((c) => (c ? { ...c, listed: true } : c));
    }
  }, [sessionBrands, created]);
  const onNewBrandPage = !embedded && tabParam === NEW_BRAND_PAGE;

  // Coming back from a Shopify connect: the API appends ?brandId=…&connected=1
  // after it has created the brand and claimed it for this account. Re-fetch
  // the session so the new store shows up in the switcher, select it, and strip
  // the params so a reload does not repeat the work.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') !== '1') return;

    // The API sends back either the brand id (it created one) or the shop
    // domain (the store was already connected and has just been claimed).
    const connectedBrandId = params.get('brandId');
    const connectedShop = params.get('shop');
    if (connectedBrandId) setSelectedBrandId(connectedBrandId);
    else if (connectedShop) setPendingShop(connectedShop);
    refresh();

    params.delete('connected');
    params.delete('brandId');
    params.delete('shop');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '')
    );
    // Runs once on mount: the params are consumed and removed immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back from Shopify with a connect token: the merchant picked the store on
  // Shopify's side, so nothing in the install said who they are. The embedded
  // app sent them here — where they ARE signed in — carrying a token that
  // proves a Shopify session for that shop asked to be linked. Trade it for
  // ownership, then select the store.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectToken = params.get('connect');
    if (!connectToken) return;

    // Strip it first: it is single-use, and leaving it in the URL means a
    // reload retries a token the API has already spent.
    params.delete('connect');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '')
    );

    let cancelled = false;
    setLinking(true);
    linkShopifyStore(connectToken)
      .then(async (res) => {
        if (cancelled) return;
        await refresh();
        if (res?.brandId) setSelectedBrandId(res.brandId);
        else if (res?.shop) setPendingShop(res.shop);
      })
      .catch((err) => {
        if (cancelled) return;
        // 409 is the one worth naming: the store belongs to another account,
        // and no amount of retrying changes that.
        setLinkError(err.status === 409 ? 'connect.alreadyLinked' : 'connect.failed');
      })
      .finally(() => {
        if (!cancelled) setLinking(false);
      });

    return () => {
      cancelled = true;
    };
    // Runs once on mount: the token is consumed and stripped immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Route guard. Embedded has no /login to send anyone to — a missing session
  // there means the page was opened outside Shopify admin, which the render
  // below states plainly instead of bouncing.
  useEffect(() => {
    // Back to this very page (and section) after signing in.
    if (!embedded && !loading && !user) {
      navigate(loginPathFor(window.location.pathname + window.location.search + window.location.hash), { replace: true });
    }
  }, [embedded, loading, user, navigate]);

  // Default the brand selector to the first brand once loaded.
  useEffect(() => {
    if (brands.length && !selectedBrandId) setSelectedBrandId(brands[0].id);
  }, [brands, selectedBrandId]);

  // Remember every selection — the switcher's and the connect flows' alike.
  useEffect(() => {
    if (selectedBrandId) storeBrand(selectedBrandId);
  }, [selectedBrandId]);

  // Select the store we just connected, as soon as the refreshed session
  // includes it. `website` carries the myshopify domain (services/stores.js).
  useEffect(() => {
    if (!pendingShop || !brands.length) return;
    const match = brands.find((b) => b.website === pendingShop);
    if (match) {
      setSelectedBrandId(match.id);
      setPendingShop(null);
    }
  }, [pendingShop, brands]);

  // Embedded, the store has no Outfino owner yet, and a portal "add a brand"
  // just went through Shopify: step out to the API once so it can read who that
  // was and claim the store, then the admin brings the app back linked. The
  // API offers claimUrl at most once per shop; the guard here only covers the
  // iframe remounting before the navigation lands.
  useEffect(() => {
    if (!embedded || !claimUrl) return;
    try {
      if (sessionStorage.getItem('outfino_claim_bounce') === claimUrl) return;
      sessionStorage.setItem('outfino_claim_bounce', claimUrl);
    } catch {
      // Storage blocked in the iframe — the API's own once-per-shop still holds.
    }
    openTopLevel(claimUrl);
  }, [embedded, claimUrl]);

  // Fall back to the first brand when the selected id is not in the list.
  //
  // Deleting a brand refreshes the session, so it leaves the list — but
  // selectedBrandId still points at it, and resolving that to null rendered a
  // blank page with a nameless "Márka" in the switcher and no way out.
  //
  // Derived rather than reset in an effect on purpose: the connect flows set a
  // brand id before the refreshed session lists it, and an effect racing them
  // would snap the selection back to the first brand just as the new store
  // arrived.
  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId) || brands[0] || null,
    [brands, selectedBrandId]
  );

  // The brand page in the URL; the bare dashboard path is Statisztikák
  // (DEFAULT_TAB), or the first page in the menu when this user cannot open it.
  const tabs = useBrandTabs(selectedBrand);
  const defaultTab = landingTab(tabs);
  // Billing is no longer a requirement (CEO, 2026-11): nothing in the portal
  // is blocked by a missing card. The two sidebar "!" nudges (no card, embed
  // not connected) are the only hint — components/portalBadges.js.
  const badges = usePortalBadges({ brand: selectedBrand, user, embedded });
  const activeTab = tabs.some((x) => x.key === tabParam) ? tabParam : defaultTab;

  // A page this brand does not offer — an old link, a brand switch to one with
  // fewer permissions, a typo — goes back to the landing page, with the URL to match.
  useEffect(() => {
    if (loading || !user || !tabParam || onNewBrandPage) return;
    if (tabs.some((x) => x.key === tabParam)) return;
    // A removed page goes to the one that replaced it, when this user has it
    // ('page#section' also scrolls to that section there).
    const [movedTab, movedAnchor] = (TAB_REDIRECTS[tabParam] || '').split('#');
    const moved = movedTab && tabs.some((x) => x.key === movedTab);
    const target = moved ? movedTab : defaultTab;
    const hash = moved && movedAnchor ? `#${movedAnchor}` : '';
    navigate(target ? `${basePath}/${target}${hash}` : basePath || '/', { replace: true });
  }, [loading, user, tabParam, tabs, defaultTab, basePath, navigate, onNewBrandPage]);

  // The Refresh button: re-fetch the session (brands, permissions, the brand's
  // own fields) and remount the open page, so it loads its data again — all in
  // place, on the same page and brand.
  const [refreshKey, setRefreshKey] = useState(0);
  async function handleRefresh() {
    badges.recheck();
    await refresh();
    setRefreshKey((k) => k + 1);
  }

  function openPortalLinked() {
    const tab = window.open('', '_blank');
    fetchSession()
      .then((s) => s.portalUrl)
      .catch(() => null)
      .then((fresh) => {
        const url = fresh || portalUrl;
        if (!url) {
          tab?.close();
          return;
        }
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
        } else {
          openTopLevel(url);
        }
      });
  }

  useEffect(() => {
    if (!embedded || linked || !returnTo) return;
    try {
      const key = `outfino_return:${returnTo}`;
      if (sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* storage blocked in the iframe; the connect token is single-use */
    }
    openTopLevel(returnTo);
  }, [embedded, linked, returnTo]);

  useEffect(() => {
    if (!embedded || linked) return undefined;
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [embedded, linked, refresh]);

  function handleLogout() {
    logout();
    navigate('/', { replace: true });
  }

  function showToast(key) {
    toastTimers.current.forEach(clearTimeout);
    setToast({ key, leaving: false });
    toastTimers.current = [
      setTimeout(() => setToast((s) => (s ? { ...s, leaving: true } : s)), 3600),
      setTimeout(() => setToast(null), 3920),
    ];
  }

  function openNewBrand() {
    navigate(`${basePath}/${NEW_BRAND_PAGE}`);
  }

  // POST /brands answered 201: select the new brand and go straight into the
  // portal. Billing is a nudge, not a gate, so there is no forced card step —
  // the "!" on Számlázás points there when they want to set it up.
  async function handleBrandCreated(result) {
    const brand = sessionBrandFrom(result, user?.id);
    if (!brand) throw new Error('no brand in the answer');
    setCreated({ brand });
    setSelectedBrandId(brand.id);
    storeBrand(brand.id);
    await refresh().catch(() => {});
    navigate(`${basePath}/${DEFAULT_TAB}`, { replace: true });
    showToast('onboarding.brandCreated');
  }

  if (loading) {
    return (
      <div className="dash-loading">
        <span>{t('shell.loading')}</span>
      </div>
    );
  }

  // Stepping out to claim the store (the claimUrl effect above): rendering the
  // dashboard would flash an unlinked store for the moment before the admin
  // navigates away. The API's /claim page carries the same screen on until the
  // app comes back linked.
  if (embedded && !linked && returnTo) {
    return (
      <div className="dash-loading">
        <div className="dash-sync">
          <span className="dash-sync__spinner" aria-hidden="true" />
          <strong>{t('connect.returning')}</strong>
          <span>{t('connect.returningHint')}</span>
          <button type="button" className="dash-sync__link" onClick={() => openTopLevel(returnTo)}>
            {t('connect.returningLink')}
          </button>
        </div>
      </div>
    );
  }

  if (embedded && claimUrl) {
    return (
      <div className="dash-loading">
        <div className="dash-sync">
          <span className="dash-sync__spinner" aria-hidden="true" />
          <strong>{t('connect.syncing')}</strong>
          <span>{t('connect.syncingHint')}</span>
        </div>
      </div>
    );
  }

  if (!user) {
    // Non-embedded falls through to the /login redirect above.
    if (!embedded) return null;
    return (
      <div className="dash-loading">
        <span>{t('shell.embeddedNoSession')}</span>
      </div>
    );
  }

  const chrome = brandChrome(selectedBrand, { embedded });

  if (onNewBrandPage) {
    return (
      <NewBrandPage
        onCreated={handleBrandCreated}
        onCancel={brands.length ? () => navigate(basePath || '/', { replace: true }) : null}
        onShopify={startShopifyConnect}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <PortalShell
      groups={BRAND_NAV
        .map((entry) => {
          // The amber "!" nudges: billing not set up (Számlázás), the
          // fit-check embed not connected yet (Beállítások). Neither blocks.
          const badgeFor = (key) =>
            (key === 'billing' && badges.cardBadge)
            || (key === 'embedded-settings' && badges.connectBadge)
            // Beüzemelés is incomplete while the button is not connected (the
            // card is optional, so it alone does not raise the "!").
            || (key === 'onboarding' && badges.connectBadge)
              ? 'warn'
              : undefined;
          const badgeTitle = (key) =>
            key === 'billing' ? t('badge.billing')
              : key === 'onboarding' ? t('badge.onboarding')
                : key === 'embedded-settings' ? t('badge.connect') : undefined;
          const link = (tab) => ({ key: tab.key, Icon: tab.Icon, label: t(`tab.${tab.key}`), to: `${basePath}/${tab.key}`, badge: badgeFor(tab.key), badgeTitle: badgeFor(tab.key) ? badgeTitle(tab.key) : undefined });
          if (entry.tab) {
            const tab = tabs.find((x) => x.key === entry.tab);
            return tab ? link(tab) : null;
          }
          const items = tabs
            .filter((tab) => tab.group === entry.group && !tab.parent)
            .map((tab) => ({ ...link(tab), sections: tabs.filter((x) => x.parent === tab.key).map(link) }));
          return items.length ? { key: entry.group, Icon: entry.Icon, label: t(`group.${entry.group}`), items } : null;
        })
        .filter(Boolean)}
      active={activeTab}
      onRefresh={handleRefresh}
      theme={theme}
      footerTop={
        !embedded && brands.length > 0 ? (
          <BrandSwitcher
            brands={brands}
            selectedBrand={selectedBrand}
            onSelect={setSelectedBrandId}
            onAddBrand={openNewBrand}
          />
        ) : null
      }
      actions={
        embedded
          ? []
          : [
              ...(chrome.shopifyAdminLink && shopifyAdminAppUrl(selectedBrand?.website)
                ? [{
                    key: 'shopify-admin',
                    label: t('shell.openInShopifyAdmin'),
                    Icon: BoxArrowUpRight,
                    onClick: () => window.open(shopifyAdminAppUrl(selectedBrand.website), '_blank', 'noopener'),
                  }]
                : []),
              { key: 'logout', label: t('shell.logout'), Icon: BoxArrowRight, onClick: handleLogout, danger: true },
            ]
      }
    >
      {embedded && portalUrl && (
        <div className="dash__return">
          <span>{t(linked ? 'shell.connectedReturn' : 'shell.notLinked')}</span>
          <button type="button" onClick={openPortalLinked}>
            {t(linked ? 'shell.openPortal' : 'shell.linkAccount')}
          </button>
        </div>
      )}

      {toast && (
        <div className={`save-toast save-toast--saved${toast.leaving ? ' is-leaving' : ''}`} role="status" data-testid="dash-toast">
          <Check2 size={18} />
          <span>{t(toast.key)}</span>
        </div>
      )}
      {linking && <div className="dash__notice">{t('connect.linking')}</div>}
      {linkError && <div className="dash__notice dash__notice--error">{t(linkError)}</div>}

      {/* documents#637 — the selected store's Shopify link, from the session's
          stores[].shopifyConnection (embedded: the session's single store).
          Renders nothing while CONNECTED. The Refresh button re-fetches the
          session, so it also re-reads this. In the portal a DISCONNECTED store
          with no reauth_url falls back to the same connect flow as "Add a
          brand", scoped to this shop; the embedded app has no Outfino token
          for that flow, so it offers only the API's link. */}
      {chrome.suspendedAlert && <BrandSuspendedBanner brand={selectedBrand} />}
      {chrome.inactiveAlert && <BrandInactiveBanner brand={selectedBrand} />}

      {chrome.connectionAlert && (
        <ShopifyConnectionBanner
          connection={selectedBrand.shopifyConnection}
          fallbackHref={!embedded && selectedBrand.website ? shopifyConnectUrl(selectedBrand.website) : null}
        />
      )}

      <div className="dash__main container-x">
        {brands.length === 0 ? (
          embedded ? null : <EmptyState onCreate={openNewBrand} />
        ) : selectedBrand ? (
          // A crash on one page shows the error card there, not a white
          // screen; another page (or Refresh) starts over.
          <PageErrorBoundary key={`${selectedBrand.id}:${activeTab}:${refreshKey}`}>
            <BrandDashboard
              key={`${selectedBrand.id}:${refreshKey}`}
              brand={selectedBrand}
              tab={activeTab}
              connected={badges.connected}
              onConnectionChange={badges.recheck}
              onNavigate={(key) => navigate(`${basePath}/${key}`)}
              // The Statisztikák "not connected" nudge sends them to the guided
              // Beüzemelés page (the one place that sets the button up end to end).
              onOpenSettings={() => navigate(`${basePath}/onboarding`)}
            />
          </PageErrorBoundary>
        ) : null}
      </div>
    </PortalShell>
  );
}

// Small logo tile with an initial fallback, reused for the trigger and rows.
// `size={null}` leaves the size to the stylesheet (the sidebar trigger scales
// it with the shell).
function BrandLogo({ brand, size = 26 }) {
  const style = size ? { width: size, height: size } : undefined;
  return brand?.logo ? (
    <img className="brand-switch__logo" style={style} src={brand.logo} alt="" />
  ) : (
    <span className="brand-switch__logo brand-switch__logo--ph" style={style}>
      {brand?.name?.[0]?.toUpperCase() || 'B'}
    </span>
  );
}

// Custom brand dropdown: shows each brand's logo, and an "add brand" row. The
// trigger is the first button of the sidebar footer, styled like the rest of
// them, so the menu opens upwards.
//
// The menu is portalled to <body> and positioned from the trigger: the sidebar
// clips its overflow, and on the collapsed icon rail the menu has to open out
// over the page, wider than the rail itself.
function BrandSwitcher({ brands, selectedBrand, onSelect, onAddBrand }) {
  const { user } = useAuth();
  const showEnv = user?.environment === 'dev';
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click / Escape / resize (the anchor moves).
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onResize() {
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setMenuPos({
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
      minWidth: Math.max(rect.width, 240),
    });
  }, [open]);

  const single = brands.length === 1;

  return (
    <div className="brand-switch" ref={ref}>
      <button
        type="button"
        className={`portal-sidebar-btn brand-switch__trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selectedBrand?.name || t('shell.brand')}
      >
        <BrandLogo brand={selectedBrand} size={null} />
        <span className="portal-nav-label brand-switch__label">{selectedBrand?.name || t('shell.brand')}</span>
        <ChevronExpand className="brand-switch__chev" />
      </button>

      {open && menuPos && createPortal(
        <div className="brand-switch__menu" role="listbox" ref={menuRef} style={menuPos}>
          {!single && (
            <div className="brand-switch__list">
              {brands.map((b) => {
                const active = b.id === selectedBrand?.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`brand-switch__item ${active ? 'is-active' : ''}`}
                    onClick={() => {
                      onSelect(b.id);
                      setOpen(false);
                    }}
                  >
                    <BrandLogo brand={b} />
                    <span className="brand-switch__item-name">{b.name}</span>
                    {showEnv && b.environment === 'dev' && (
                      <span className="brand-switch__env">{t('shell.envDev')}</span>
                    )}
                    {active && <Check2 size={16} className="brand-switch__check" />}
                  </button>
                );
              })}
            </div>
          )}
          <button
            type="button"
            className="brand-switch__add"
            onClick={() => {
              setOpen(false);
              onAddBrand();
            }}
          >
            <span className="brand-switch__add-icon">
              <PlusLg size={14} />
            </span>
            {t('shell.newBrand')}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  const { t } = useLocale();
  return (
    <div className="empty-state">
      <span className="empty-state__icon">
        <Shop size={30} />
      </span>
      <h1 className="empty-state__title">{t('empty.title')}</h1>
      <p className="empty-state__body">{t('empty.body')}</p>
      <button className="btn-o btn-o--dark" onClick={onCreate}>
        <PlusLg size={16} style={{ marginRight: 8 }} />
        {t('empty.create')}
      </button>
    </div>
  );
}
